import { useEffect, useMemo, useState } from "react";
import "./AdminCourtsOccupancyModule.scss";
import { toast } from "react-toastify";
import {
  CourtOccupancyStatisticsExport,
  OccupancyStatisticsExport,
} from "../../../types/api";
import StatisticsFilterList from "../StatisticsFilterList/StatisticsFilterList";

import { createStatisticsFilterStore } from "../Stores/statisticsFilterStoreFactory";
import { ReportTableSection } from "../Tables/ReportTableSection";
import { MembertypeChart } from "../Charts/MembertypeChart";
import { GuestChart } from "../Charts/GuestChart";
import { useStore } from "zustand/react";

interface AdminCourtsOccupancyModuleProps {
  courtsOccupancyData: CourtOccupancyStatisticsExport;
  courtOccupancyFilterStore: ReturnType<typeof createStatisticsFilterStore>;
  startDate: string;
  endDate: string;
  selectedWeekdays: number[];
}

const AdminCourtsOccupancyModule = ({
  courtsOccupancyData,
  courtOccupancyFilterStore,
  startDate,
  endDate,
  selectedWeekdays,
}: AdminCourtsOccupancyModuleProps) => {
  const chosenCoaches = useStore(
    courtOccupancyFilterStore,
    (state) => state.selectedCoaches
  );
  function countWeekdaysFromIntervals(startDate: Date, endDate: Date) {
    const counts = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    const start = new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate()
      )
    );
    const end = new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate()
      )
    );

    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      const dayOfWeek = d.getUTCDay(); // 0 = Sunday
      const isoDay = dayOfWeek;

      if (selectedWeekdays.includes(isoDay)) {
        switch (isoDay) {
          case 1:
            counts.monday++;
            break;
          case 2:
            counts.tuesday++;
            break;
          case 3:
            counts.wednesday++;
            break;
          case 4:
            counts.thursday++;
            break;
          case 5:
            counts.friday++;
            break;
          case 6:
            counts.saturday++;
            break;
          case 0:
            counts.sunday++;
            break;
        }
      }
    }

    return counts;
  }

  const [weekdayCounts, setWeekdayCounts] = useState({
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  });

  const totalDays = Object.values(weekdayCounts).reduce(
    (acc, count) => acc + count,
    0
  );
  useEffect(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const counts = countWeekdaysFromIntervals(start, end);
    setWeekdayCounts(counts);
  }, [startDate, endDate, selectedWeekdays, chosenCoaches]);
  const [chartType, setChartType] = useState<"line" | "pie">("line");

  return (
    <div className="admin-courts-occupancy-module">
      <StatisticsFilterList
        useStore={courtOccupancyFilterStore}
        useTimeintervals={true}
        usePermissions={false}
        useCourts={true}
        useCoaches={true}
        useActivityColors={true}
      />
      <ReportTableSection
        title="Beläggning - Banor"
        children={
          chosenCoaches.length > 0 ? (
            <div className="warning-text text-s secondary-text">
              <i className="fa-regular fa-triangle-exclamation warning-icon"></i>{" "}
              Om tränare vald, räknas endast bokningar med tränare
            </div>
          ) : null
        }
        columns={[
          { key: "courtName", label: "Namn på bana" },
          { key: "timespan", label: "Tidsperiod" },
          {
            key: "monday",
            label: `Måndag (${weekdayCounts.monday})`,
            align: "left",
          },
          {
            key: "tuesday",
            label: `Tisdag (${weekdayCounts.tuesday})`,
            align: "left",
          },
          {
            key: "wednesday",
            label: `Onsdag (${weekdayCounts.wednesday})`,
            align: "left",
          },
          {
            key: "thursday",
            label: `Torsdag (${weekdayCounts.thursday})`,
            align: "left",
          },
          {
            key: "friday",
            label: `Fredag (${weekdayCounts.friday})`,
            align: "left",
          },
          {
            key: "saturday",
            label: `Lördag (${weekdayCounts.saturday})`,
            align: "left",
          },
          {
            key: "sunday",
            label: `Söndag (${weekdayCounts.sunday})`,
            align: "left",
          },
          {
            key: "average",
            label: `Genomsnitt (${totalDays})`,
            align: "left",
          },
        ]}
        rows={courtsOccupancyData && courtsOccupancyData.data}
        useCalcType={true}
        useToggleType={false}
        onToggleType={(isPie: boolean) => setChartType(isPie ? "pie" : "line")}
        useStore={courtOccupancyFilterStore}
        isLoading={false}
      />
      <div className="occupancy-spacer" />
    </div>
  );
};
export default AdminCourtsOccupancyModule;
